import React from "react";
import { CTBRetest } from "../../../utils/api/endpoints/retests/parseRetests";
import { useRetestCardData } from "../../../utils/hooks/retests/useRetestCardData";
import { FiClock, FiCheck, FiX, FiExternalLink } from "react-icons/fi";
import RichTextRenderer from "../../common/RichTextRenderer";
import AssetTypeIcon from "../../../assets/icons/asset_type.svg";
import CvssScoreIcon from "../../../assets/icons/cvss_score.svg";
import OpenedDateIcon from "../../../assets/icons/opned_date.svg";

const stripRichText = (content: string): string => {
  if (!content) return "";
  // 1. Remove markdown images: ![description](url)
  let text = content.replace(/!\[.*?\]\(.*?\)/g, '');
  // 2. Remove markdown links but keep the text: [link text](url)
  text = text.replace(/\[(.*?)\]\(.*?\)/g, '$1');
  // 3. Remove HTML tags
  text = text.replace(/<[^>]*>/g, '');
  // 4. Remove other markdown formatting characters: headers, bold, italic, code blocks, etc.
  text = text.replace(/^#+\s+/gm, '')
             .replace(/[\*_~`#]+/g, '')
             .trim();
  return text;
};

interface RetestCardProps {
  retest: CTBRetest;
  onClick: () => void;
}



const RetestCard: React.FC<RetestCardProps> = ({ retest, onClick }) => {
  const retestData = useRetestCardData(retest);

  // Status configuration
  const getStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case "retest requested":
        return {
          label: "Retest Requested",
          icon: <FiClock className="w-4 h-4" />,
          bgColor: "bg-blue-100",
          textColor: "text-blue-800",
          borderColor: "border-blue-200"
        };
      case "fix verified":
        return {
          label: "Fix Verified",
          icon: <FiCheck className="w-4 h-4" />,
          bgColor: "bg-green-100",
          textColor: "text-green-800",
          borderColor: "border-green-200"
        };
      case "fix failed":
        return {
          label: "Fix Failed",
          icon: <FiX className="w-4 h-4" />,
          bgColor: "bg-red-100",
          textColor: "text-red-800",
          borderColor: "border-red-200"
        };
      case "retest closed":
      case "close retest":
        return {
          label: "Retest Closed",
          icon: <FiClock className="w-4 h-4" />,
          bgColor: "bg-gray-100",
          textColor: "text-gray-800",
          borderColor: "border-gray-200"
        };
      default:
        return {
          label: status,
          icon: <FiClock className="w-4 h-4" />,
          bgColor: "bg-gray-100",
          textColor: "text-gray-800",
          borderColor: "border-gray-200"
        };
    }
  };

  // Severity configuration
  const getSeverityConfig = (severity: string) => {
    const sevLower = (severity || "").toLowerCase().trim();
    switch (sevLower) {
      case "critical":
        return {
          label: "Critical",
          dotColor: "bg-red-500",
          textColor: "text-red-800",
          bgColor: "bg-red-100"
        };
      case "high":
        return {
          label: "High",
          dotColor: "bg-orange-500",
          textColor: "text-orange-800",
          bgColor: "bg-orange-100"
        };
      case "medium":
        return {
          label: "Medium",
          dotColor: "bg-yellow-500",
          textColor: "text-yellow-800",
          bgColor: "bg-yellow-100"
        };
      case "low":
        return {
          label: "Low",
          dotColor: "bg-green-500",
          textColor: "text-green-800",
          bgColor: "bg-green-100"
        };
      case "info":
      case "none":
      case "":
        return {
          label: "Info",
          dotColor: "bg-gray-500",
          textColor: "text-gray-800",
          bgColor: "bg-gray-100"
        };
      default:
        return {
          label: severity,
          dotColor: "bg-gray-500",
          textColor: "text-gray-800",
          bgColor: "bg-gray-100"
        };
    }
  };

  // Comment box configuration based on status
  const getCommentBoxConfig = (status: string, comment: string) => {
    const hasComment = comment && comment.trim() !== "";
    if (status.toLowerCase() === "retest requested") {
      // Use Retest In Process colors, no title/description
      const processStatus = getStatusConfig("retest in process");
      return {
        bgColor: processStatus.bgColor,
        textColor: processStatus.textColor,
        content: "No updates"
      };
    }
    switch (status.toLowerCase()) {
      case "fix verified":
        return {
          bgColor: "bg-green-100",
          textColor: "text-green-700",
          content: hasComment ? comment : "Fix Details: Updated successfully"
        };
      case "fix failed":
        return {
          bgColor: "bg-red-100",
          textColor: "text-red-700",
          content: hasComment ? comment : "Fix Failed Reason: Unable to verify fix"
        };
      default:
        return {
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          content: hasComment ? comment : "No Update"
        };
    }
  };

  const statusConfig = getStatusConfig(retestData.status);
  // Use severityCategory if available, otherwise fall back to severity, otherwise default to "medium"
  const severityToUse = retestData.severityCategory || retestData.severity || "medium";
  const severityConfig = getSeverityConfig(severityToUse);

  // Check if we have a valid comment (not empty, not "N/A", etc.)
  const hasValidComment = retestData.latest_comment &&
    retestData.latest_comment.trim() !== "" &&
    retestData.latest_comment.toUpperCase() !== "N/A" &&
    retestData.latest_comment.toUpperCase() !== "<P></P>";

  // Use the statusConfig.label which already has the correct formatted title
  // Instead of re-formatting the status string
  const statusTitle = statusConfig.label;
  const commentConfig = getCommentBoxConfig(retestData.status || "", (hasValidComment ? retestData.latest_comment : "") || "");



  // Format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 px-6 pt-6 pb-0 hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full"
      onClick={onClick}
    >
      {/* Header with Status and Severity */}
      <div className="flex justify-between items-start mb-4">
        <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${statusConfig.bgColor} ${statusConfig.textColor}`}>
          {statusConfig.icon}
          {statusConfig.label}
        </div>
        <div className={`px-3 py-1 rounded-md text-sm font-medium flex items-center gap-2 ${severityConfig.bgColor} ${severityConfig.textColor}`}>
          <div className={`w-2 h-2 rounded-full ${severityConfig.dotColor}`}></div>
          {severityConfig.label}
        </div>
      </div>

      {/* Content wrapper - grows to push footer to bottom */}
      <div className="flex-1">
        {/* Border separator */}
        <div className="border-t border-gray-100 mb-4 -mx-6"></div>

        {/* Vulnerability Title */}
        <h3 className="text-base font-semibold text-black mb-2 line-clamp-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <RichTextRenderer content={retestData.report_name || ""} />
        </h3>

        {/* Description */}
        <div className="text-[#4B5563] text-sm mb-4 line-clamp-2 font-light overflow-hidden">
          {stripRichText(retestData.description || "No description available")}
        </div>

        {/* Key Details */}
        <div className="mb-4">
          <div className="grid grid-cols-2 gap-8">
            {/* First Row */}
            <div className="flex items-start gap-2 text-sm">
              <img src={AssetTypeIcon} alt="Asset Type" className="w-4 h-4 mt-0.5" />
              <span><span className="font-normal text-[#010D2C]">Asset:</span> <span className="font-light text-[#4B5563]">{retestData.asset_name || retestData.assetType || "Unknown"}</span></span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <img src={CvssScoreIcon} alt="CVSS" className="w-4 h-4 mt-0.5" />
              <span><span className="font-normal text-[#010D2C]">CVSS:</span> <span className="font-light text-[#4B5563]">{retestData.cvssScore || "0.0"}</span></span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-8 mt-2">
            {/* Second Row */}
            <div className="flex items-start gap-2 text-sm">
              <img src={OpenedDateIcon} alt="Opened" className="w-4 h-4 mt-0.5" />
              <span><span className="font-normal text-[#010D2C]">Opened:</span> <span className="font-light text-[#4B5563]">{formatDate(retestData.createdAt)}</span></span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <img src={OpenedDateIcon} alt="Latest Update" className="w-4 h-4 mt-0.5" />
              <span><span className="font-normal text-[#010D2C]">Latest Update:</span> <span className="font-light text-[#4B5563]">{formatDate(retestData.updatedAt)}</span></span>
            </div>
          </div>
        </div>

        <div className={`p-3 rounded-md mb-4 ${commentConfig.bgColor}`}>
          {retestData.status.toLowerCase() === "retest requested"
            ? <div className={`text-sm font-normal ${commentConfig.textColor} text-center m-0 line-clamp-3 overflow-hidden`}>{stripRichText(commentConfig.content || "")}</div>
            : <>
              <p className={`text-sm font-semibold ${statusConfig.textColor} mb-1`}>{statusTitle}</p>
              <div className={`text-sm ${statusConfig.textColor} line-clamp-3 overflow-hidden`}>
                {stripRichText((hasValidComment ? retestData.latest_comment : commentConfig.content) || "")}
              </div>
            </>
          }
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center border-t border-gray-100 bg-[#010D2C] -mx-6 px-6 py-4 mt-auto rounded-b-lg">
        <span className="text-sm text-[#FFFFFF] font-normal">
          {retestData.programName || retestData.program_name}
        </span>
        <div className="flex items-center gap-1 text-[#58CC02] hover:text-[#58CC02]/80 text-sm font-medium">
          <span>View Details</span>
          <FiExternalLink className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

export default RetestCard;
